import { Box, Button, Drawer, Stack, Typography } from "@mui/material";
import { Component, ReactNode } from "react";
import { isAuthorizationError } from "utils/errorUtils";

interface WasteStreamAccessDeniedProps {
  onClose: () => void;
}

interface WasteStreamAccessDeniedDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface WasteStreamAccessBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface WasteStreamAccessBoundaryState {
  error: unknown;
}

export function WasteStreamAccessDenied({ onClose }: WasteStreamAccessDeniedProps) {
  return (
    <Box p={3}>
      <Stack spacing={2} alignItems="flex-start">
        <Typography variant="h6">You don't have access to this waste stream.</Typography>
        <Typography variant="body1">
          It belongs to a facility you aren't assigned to, or it no longer exists. Ask an
          organization admin if you need access.
        </Typography>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Box>
  );
}

/** Same shell as the stream details drawer. */
export function WasteStreamAccessDeniedDrawer({
  open,
  onClose,
}: WasteStreamAccessDeniedDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: 600, maxWidth: "80%" } }}
    >
      <WasteStreamAccessDenied onClose={onClose} />
    </Drawer>
  );
}

/** Explains a refused stream inside the drawer; any other error still reaches the route boundary. */
export class WasteStreamAccessBoundary extends Component<
  WasteStreamAccessBoundaryProps,
  WasteStreamAccessBoundaryState
> {
  state: WasteStreamAccessBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    if (this.state.error === null) return this.props.children;
    if (isAuthorizationError(this.state.error)) {
      return <WasteStreamAccessDenied onClose={this.props.onClose} />;
    }
    throw this.state.error;
  }
}
